import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'primereact/tooltip';
import { ChecklistSection as ChecklistSectionType } from '../../data/checklistData';
import PlaybookTask from './PlaybookTask';

const stripWrappingParentheses = (value: string): string =>
  value.replace(/^\s*\((.*)\)\s*$/, '$1');

const IMPACT_FILTERS = [
  { key: 'all', labelKey: 'geoChecklist.pb.filterAll' },
  { key: 'high', labelKey: 'geoChecklist.pb.impact.high' },
  { key: 'medium', labelKey: 'geoChecklist.pb.impact.medium' },
  { key: 'low', labelKey: 'geoChecklist.pb.impact.low' },
] as const;

type ImpactFilter = (typeof IMPACT_FILTERS)[number]['key'];

const IMPACT_RANK: Record<string, number> = { high: 0, medium: 1, low: 2 };

interface PlaybookSectionProps {
  section: ChecklistSectionType;
  checkedTasks: Record<string, string>;
  onToggle: (taskId: string) => void;
}

/** Section tile (Key-Takeaways style): in-box title, info tooltip, per-section impact
 *  filter, then a scrollable task list (max ~3 rows). Tasks are sorted incomplete-first
 *  then by impact (high → low), with completed at the bottom; reorders animate (FLIP). */
const PlaybookSection: React.FC<PlaybookSectionProps> = ({ section, checkedTasks, onToggle }) => {
  const { t } = useTranslation();
  const [impactFilter, setImpactFilter] = useState<ImpactFilter>('all');

  const tooltip = section.subtitleKey
    ? stripWrappingParentheses(t(section.subtitleKey, { defaultValue: section.defaultSubtitle }))
    : null;
  const infoId = `section-info-${section.id}`;

  const tasks = (
    impactFilter === 'all'
      ? section.tasks
      : section.tasks.filter((task) => task.impact === impactFilter)
  )
    .slice()
    .sort((a, b) => (IMPACT_RANK[a.impact ?? ''] ?? 3) - (IMPACT_RANK[b.impact ?? ''] ?? 3)); // high → low

  return (
    <div id={`section-${section.id}`} className="stats__panel">
      <div className="page-box__title page-box__title--flex">
        {t(section.titleKey, { defaultValue: section.defaultTitle })}
        {tooltip && (
          <>
            <span
              id={infoId}
              className="ico--info ico--xs playbook-section__info"
              data-pr-tooltip={tooltip}
              data-pr-position="top"
            />
            <Tooltip target={`#${infoId}`} />
          </>
        )}
      </div>
      <div className="stats-box playbook-section">
        <div className="stats-box__content playbook-section__content">
          <div className="playbook-section__filters">
            {IMPACT_FILTERS.map((f) => {
              const group =
                f.key === 'all' ? section.tasks : section.tasks.filter((tk) => tk.impact === f.key);
              if (group.length === 0) return null;
              const done = group.filter((tk) => tk.id in checkedTasks).length;
              return (
                <button
                  key={f.key}
                  type="button"
                  className={`playbook-section__filter${impactFilter === f.key ? ' playbook-section__filter--active' : ''}`}
                  onClick={() => setImpactFilter(f.key)}
                >
                  {t(f.labelKey)}
                  <span className="playbook-section__filter-count">
                    {done}/{group.length}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="playbook-section__list">
            {tasks.map((task) => (
              <PlaybookTask
                key={task.id}
                task={task}
                isChecked={task.id in checkedTasks}
                onToggle={() => onToggle(task.id)}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlaybookSection;
